<template>
  <el-dialog
    v-model="visible"
    :title="$t('chat.groups.create_title')"
    width="440px"
    :append-to-body="true"
    :close-on-click-modal="false"
    destroy-on-close
    @open="onOpen"
  >
    <div class="space-y-4">
      <!-- Title input -->
      <div>
        <label class="block text-xs font-semibold text-text-muted mb-1.5">
          {{ $t('chat.groups.title_label') }}
        </label>
        <input
          ref="titleInputEl"
          v-model="title"
          type="text"
          maxlength="80"
          class="w-full px-3 py-2 rounded-md bg-black/30 border border-white/5 text-sm text-white placeholder:text-text-dim outline-none focus:border-primary/50 transition-colors"
          :placeholder="$t('chat.groups.title_placeholder')"
        />
      </div>

      <!-- Friend multi-select -->
      <div>
        <label class="block text-xs font-semibold text-text-muted mb-1.5">
          {{ $t('chat.groups.members_label', { count: selected.size }) }}
        </label>
        <div v-if="friends.length === 0" class="text-[11px] text-text-dim italic px-1 py-4 text-center">
          {{ $t('chat.groups.no_friends') }}
        </div>
        <div
          v-else
          class="max-h-64 overflow-y-auto scrollbar-thin rounded-md border border-white/5 divide-y divide-white/5"
        >
          <label
            v-for="f in friends"
            :key="f.id"
            class="flex items-center gap-2.5 px-2.5 py-2 hover:bg-white/[0.03] cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="selected.has(f.user.id)"
              class="shrink-0"
              @change="toggle(f.user.id)"
            />
            <img
              :src="f.user.avatar || '/images/default-avatar.svg'"
              class="w-7 h-7 rounded-full object-cover shrink-0"
              :class="f.user.openForCollabs ? 'ring-2 ring-cyan-400/60' : ''"
              :alt="f.user.name"
              :title="f.user.openForCollabs ? $t('chat.badges.collab_tooltip') : ''"
            />
            <span class="text-sm text-white truncate">{{ f.user.name }}</span>
            <span
              v-if="f.user.openForCollabs"
              class="shrink-0 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-cyan-400/15 text-cyan-300 border border-cyan-400/30"
            >
              {{ $t('chat.badges.collab') }}
            </span>
          </label>
        </div>
      </div>

      <p v-if="errorMsg" class="text-[11px] text-red-400">{{ errorMsg }}</p>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <button
          type="button"
          class="px-3 py-1.5 rounded-md text-sm text-text-muted hover:text-white bg-white/5"
          :disabled="busy"
          @click="visible = false"
        >
          {{ $t('chat.groups.cancel') }}
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-md text-sm font-semibold bg-primary text-black hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canSubmit || busy"
          @click="submit"
        >
          {{ busy ? '…' : $t('chat.groups.create_submit') }}
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, reactive, nextTick, watch } from 'vue'
import { useFriends } from '~/composables/chat/useFriends'
import { useChatUI } from '~/composables/chat/useChatUI'

/**
 * Create-a-group dialog. Picks a title + multi-selects accepted friends,
 * then POSTs /api/chat/groups. The newly-created thread opens in the DMs
 * tab on success.
 *
 * The server returns the hydrated thread row and also pushes
 * `thread.created` on each participant's user channel, so other tabs of
 * the creator + every participant see the new group without a manual
 * refresh.
 */

const props = defineProps({
  modelValue: { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue', 'created'])

const visible = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v)
})

// `useFriends()` auto-loads the friends list on first access if it hasn't
// been loaded yet — no explicit ensureLoaded call needed here.
const { friends, loading } = useFriends()
const { openDMThread, setTab } = useChatUI()

const title = ref('')
const selected = reactive(new Set())
const busy = ref(false)
const errorMsg = ref('')
const titleInputEl = ref(null)

async function onOpen() {
  errorMsg.value = ''
  title.value = ''
  selected.clear()
  await nextTick()
  titleInputEl.value?.focus()
}

function toggle(userId) {
  if (selected.has(userId)) selected.delete(userId)
  else selected.add(userId)
}

const canSubmit = computed(() =>
  title.value.trim().length > 0 && selected.size >= 1
)

async function submit() {
  if (!canSubmit.value || busy.value) return
  busy.value = true
  errorMsg.value = ''
  try {
    const res = await $fetch('/api/chat/groups', {
      method: 'POST',
      body: {
        title: title.value.trim(),
        participantIds: [...selected]
      }
    })
    emit('created', res.thread)
    // Jump straight to the new group's thread view
    if (res.thread?.id) {
      setTab('dms')
      openDMThread(res.thread.id)
    }
    visible.value = false
  } catch (err) {
    errorMsg.value = err?.data?.statusMessage || err?.message || 'Failed to create group'
  } finally {
    busy.value = false
  }
}

// Reset state when closed so reopening is fresh
watch(visible, (v) => {
  if (!v) {
    title.value = ''
    selected.clear()
    errorMsg.value = ''
  }
})
</script>
